// src/pages/Sign.jsx
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { useToast } from "../components/Toast/useToast.jsx";
import { db, storage, serverTimestamp, auth } from "../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

// Import utilities
import {
  getSignatureRectForPage,
  isExpired,
  buildSignedPath,
  buildSignedDocumentEmail,
  formatDate,
  COLLECTIONS,
  REQUEST_STATUS,
  PDF_TEMPLATE,
} from "../utils";
import { detectLabelPositions } from "../utils/pdfPositionDetector";

const DEBUG = false;

// 🔧 Constantes para Partes de Trabajo (posicionamiento en PDF)
const BASE = { w: 595.28, h: 841.89 }; // A4 en puntos

const PLACEMENT = {
  pageIndex: 0,
  operarios:   { x: 80,  y: 214, w: 480, size: 11, leading: 14 },
  operaciones: { x: 80,  y: 188, w: 480, size: 11, leading: 14 },
  firmaRect:   { x: 360, y: 55,  w: 200, h: 100 },  // Posición original
  nombre: { x: 130, y: 66, size: 11 },
  dni:    { x: 130, y: 48, size: 11 },
  signedInfo: { xCenter: BASE.w / 2, y: 48, size: 10 },
};

const LABEL_LEFT_X = 28, LABEL_GAP = 1, LABEL_SIZE = 12, VALUE_SIZE = 12;
const LABELS = {
  km:         { text: "Km totales:",                          y: 270, offsetX: -18 },
  horasDespl: { text: "Horas desplazamiento (POR OPERARIO):", y: 252, offsetX: -28 },
  horasObra:  { text: "Horas obra (POR OPERARIO):",           y: 235, offsetX: -28 },
  operarios:  { text: "Operarios:",                           y: 214, offsetX:  -6 },
};

const sx = (w) => w / BASE.w;
const sy = (h) => h / BASE.h;

// Función para dibujar texto multilínea
function drawMultiline(page, font, text, x, y, w, size = 11, leading = 14) {
  const words = String(text ?? "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(test, size) > w) {
      if (line) lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.forEach((ln, i) => {
    page.drawText(ln, { x, y: y - i * leading, size, font, color: rgb(0, 0, 0) });
  });
}

// Función para calcular posición X después de etiqueta
function valueXAfterLabel(font, key) {
  const { text, offsetX = 0 } = LABELS[key];
  const labelWidth = font.widthOfTextAtSize(text, LABEL_SIZE);
  return LABEL_LEFT_X + offsetX + labelWidth + LABEL_GAP;
}

export default function Sign() {
  const { id } = useParams();
  const { showToast } = useToast();
  const sigRef = useRef(null);
  const fsSigRef = useRef(null);
  const containerRef = useRef(null);

  const [pdfURL, setPdfURL] = useState("");
  const [name, setName] = useState("");
  const [dni, setDni] = useState("");

  // 🔧 Campos adicionales para Partes de Trabajo
  const [documentType, setDocumentType] = useState("albaran"); // "albaran" o "parte_trabajo"
  const [obra, setObra] = useState(""); // 🏗️ NUEVO: Campo obra para agrupar partes
  const [km, setKm] = useState("");
  const [hDespl, setHDespl] = useState("");
  const [hObra, setHObra] = useState("");
  const [operarios, setOperarios] = useState("");
  const [operaciones, setOperaciones] = useState("");

  // 📋 Datos adicionales de pre-rellenado desde solicitud
  const [codigoObra, setCodigoObra] = useState("");
  const [partidaTrabajo, setPartidaTrabajo] = useState("");
  const [fechasActuacion, setFechasActuacion] = useState("");
  const [numeroOferta, setNumeroOferta] = useState("");
  const [personaContacto, setPersonaContacto] = useState("");
  const [direccionObra, setDireccionObra] = useState("");
  const [trabajosRealizar, setTrabajosRealizar] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doneURL, setDoneURL] = useState("");
  const [emailToSend, setEmailToSend] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [request, setRequest] = useState(null);

  // Canvas simple
  const [fsOpen, setFsOpen] = useState(false);
  const sigBoxRef = useRef(null);
  const nativeCanvasRef = useRef(null); // canvas nativo para firma principal
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Detector móvil simple
  const [isMobile, setIsMobile] = useState(false);

  const SEND_URL = import.meta.env.VITE_SEND_EMAIL_URL?.trim();
  const SEND_SIGNED_URL = import.meta.env.VITE_SEND_SIGNED_URL?.trim();

  // Detector móvil simple
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Inicializar canvas nativo cuando el componente monta o cambia tamaño
  useEffect(() => {
    const box = sigBoxRef.current;
    const canvas = nativeCanvasRef.current;
    if (!box || !canvas) return;

    const initCanvas = () => {
      const rect = box.getBoundingClientRect();
      // Limitar al ancho visible del viewport para evitar que el canvas se salga en móvil
      const maxW = window.innerWidth - (isMobile ? 32 : 64);
      const w = Math.round(Math.min(rect.width, maxW)) || 600;
      const h = isMobile ? 200 : 180;
      if (canvas.width === w && canvas.height === h) return;
      canvas.width  = w;
      canvas.height = h;
      canvas.style.width  = "100%";
      canvas.style.height = h + "px";
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000";
      ctx.lineWidth   = 2.5;
      ctx.lineCap     = "round";
      ctx.lineJoin    = "round";
    };

    initCanvas();
    const ro = new ResizeObserver(initCanvas);
    ro.observe(box);
    return () => ro.disconnect();
  }, [isMobile]);

  // Obtener coordenadas correctas (mouse o touch) relativas al canvas
  const getPos = (e) => {
    const canvas = nativeCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // Escalar coordenadas CSS → coordenadas del buffer del canvas
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top)  * scaleY,
    };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const canvas = nativeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const onPointerMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = nativeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const onPointerUp = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearNativeCanvas = () => {
    const canvas = nativeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Compatibilidad: sigRef.current.isEmpty() y sigRef.current.toDataURL()
  // Usados en la función sign() — los redirigimos al canvas nativo
  useEffect(() => {
    if (sigRef.current) return; // si react-signature-canvas sigue montado, no hacer nada
  }, []);

  // Cargar solicitud + PDF
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.REQUESTS, id));
        if (!snap.exists()) {
          showToast({ type: "error", message: "Solicitud no encontrada o expirada" });
          setLoading(false);
          return;
        }
        const data = snap.data();
        setRequest({ id, ...data });

        // 🔧 Detectar tipo de documento
        setDocumentType(data.documentType || "albaran");

        if (isExpired(data.expiresAt)) {
          showToast({ type: "error", message: "Este enlace ha caducado" });
          setLoading(false);
          return;
        }
        if (data.status === REQUEST_STATUS.SIGNED) {
          if (data.signedPath) {
            const done = await getDownloadURL(ref(storage, data.signedPath));
            setDoneURL(done);
          }
          // Pre-rellenar email del cliente aunque ya esté firmado
          if (data.emailCliente) setEmailToSend(data.emailCliente);
          setLoading(false);
          return;
        }

        // Cargar datos comunes
        setName(data.signerName ?? "");
        setDni(data.signerDni ?? "");
        
        // 🏗️ Cargar obra para todos los documentos
        setObra(data.obra ?? "");
        
        // 🔧 Cargar datos de parte de trabajo si existen
        if (data.documentType === "parte_trabajo") {
          setKm(data.kmTotales ?? "");
          setHDespl(data.horasDespl ?? "");
          setHObra(data.horasObra ?? "");
          setOperarios(data.operarios ?? "");
          setOperaciones(data.operaciones ?? "");
        }
        
        // 📋 Pre-rellenar datos desde solicitud si existen
        if (data.prefilledData) {
          const prefilled = data.prefilledData;
          
          // Datos de la obra
          setCodigoObra(prefilled.codigoObra || "");
          setPartidaTrabajo(prefilled.partidaTrabajo || "");
          setFechasActuacion(prefilled.fechasActuacion || "");
          setNumeroOferta(prefilled.numeroOferta || "");
          
          // Datos de contacto
          setPersonaContacto(prefilled.personaContacto || "");
          setDireccionObra(prefilled.direccionObra || "");
          
          // Trabajos a realizar
          setTrabajosRealizar(prefilled.trabajosRealizar || "");
          
          // Pre-rellenar operarios si no están ya definidos
          if (!data.operarios && prefilled.operariosAsignados) {
            setOperarios(prefilled.operariosAsignados);
          }
          
          // Pre-rellenar operaciones con trabajos a realizar si no están definidos
          if (!data.operaciones && prefilled.trabajosRealizar) {
            setOperaciones(prefilled.trabajosRealizar);
          }
        }
        
        const url = await getDownloadURL(ref(storage, data.path));
        // Descargar como blob y crear URL con el nombre original para que el visor lo muestre correctamente
        try {
          const blobResp = await fetch(url);
          const blob = await blobResp.blob();
          const fileName = encodeURIComponent(data.originalName || "documento.pdf");
          // Usar el blob URL y añadir el nombre como fragmento para que Chrome lo muestre
          const blobUrl = URL.createObjectURL(blob);
          setPdfURL(blobUrl + "#" + fileName);
        } catch {
          setPdfURL(url);
        }
      } catch (e) {
        console.error("Error cargando documento:", e);
        if (e.code === "storage/unauthorized" || e.code === "storage/unauthenticated") {
          showToast({ type: "error", message: "Error de permisos al cargar el PDF. Contacta con el administrador." });
        } else if (e.code === "storage/object-not-found") {
          showToast({ type: "error", message: "El archivo PDF no se encontró en el servidor." });
        } else if (e.code === "permission-denied") {
          showToast({ type: "error", message: "Sin permisos para acceder al documento." });
        } else {
          showToast({ type: "error", message: "Error cargando el documento" });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const shareWhatsApp = () => {
    if (!doneURL) {
      showToast({ type: "error", message: "Primero firma el documento" });
      return;
    }
    const texto = encodeURIComponent(`Aquí tienes el documento firmado: ${doneURL}`);
    window.open(`https://wa.me/?text=${texto}`, "_blank", "noopener");
  };

  const sendByEmail = async () => {
    if (!doneURL) { showToast({ type: "error", message: "Primero firma el documento" }); return; }
    const email = emailToSend.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast({ type: "error", message: "Introduce un email válido" }); return;
    }
    setSendingEmail(true);
    try {
      const sendUrl = SEND_SIGNED_URL || SEND_URL;
      if (!sendUrl) throw new Error("URL de envío no configurada");
      let authHeaders = {};
      const user = auth.currentUser;
      if (user && !user.isAnonymous) {
        try { authHeaders = { "Authorization": `Bearer ${await user.getIdToken(true)}` }; } catch(e) {}
      }
      const docType = documentType === "parte_trabajo" ? "Parte de Trabajo" : "Albarán";
      // Para envío manual siempre usar SEND_URL (sin adjunto) — más fiable
      const manualSendUrl = SEND_URL || SEND_SIGNED_URL;
      const res = await fetch(manualSendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          to: [email],
          subject: `${docType} Firmado`,
          html: buildSignedDocumentEmail({ documentName: request?.originalName || "documento", signerName: name, signerDni: dni, signedDate: formatDate(new Date()), downloadUrl: doneURL }),
          requestId: id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || "Error enviando");
      showToast({ type: "success", message: `Email enviado a ${email}` });
      setEmailToSend("");
    } catch(e) {
      showToast({ type: "error", message: `Error: ${e.message}` });
    } finally {
      setSendingEmail(false);
    }
  };

  const takeFsSignature = () => {
    if (!fsSigRef.current) return setFsOpen(false);
    const dataUrl = fsSigRef.current.toDataURL();
    if (sigRef.current) {
      sigRef.current.clear();
      sigRef.current.fromDataURL(dataUrl);
    }
    setFsOpen(false);
  };

  const sign = async () => {
    if (!name.trim() || !dni.trim()) {
      showToast({ type: "error", message: "Escribe nombre y DNI" });
      return;
    }
    // Validaciones extra para partes de trabajo
    if (documentType === "parte_trabajo") {
      if (!km.trim()) {
        showToast({ type: "error", message: "Los kilómetros totales son obligatorios" });
        return;
      }
      if (!hDespl.trim()) {
        showToast({ type: "error", message: "Las horas de desplazamiento son obligatorias" });
        return;
      }
      if (!hObra.trim()) {
        showToast({ type: "error", message: "Las horas de obra son obligatorias" });
        return;
      }
      if (!operarios.trim()) {
        showToast({ type: "error", message: "El campo operarios es obligatorio" });
        return;
      }
    }
    if (!hasSignature) {
      showToast({ type: "error", message: "Dibuja tu firma" });
      return;
    }
    if (saving) return;

    try {
      setSaving(true);
      const bytes = await fetch(pdfURL).then(r => r.arrayBuffer());
      const pdfDoc = await PDFDocument.load(bytes);
      const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const page = pdfDoc.getPages()[Math.min(PDF_TEMPLATE.PAGE_INDEX, pdfDoc.getPages().length - 1)];

      // Firma PNG — recortada al bounding box real para que llene el recuadro
      const dataURL = (() => {
        const srcCanvas = nativeCanvasRef.current;
        if (!srcCanvas) return "";
        const ctx = srcCanvas.getContext("2d");
        const w = srcCanvas.width;
        const h = srcCanvas.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        // Calcular bounding box de los píxeles no transparentes
        let minX = w, minY = h, maxX = 0, maxY = 0;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const alpha = data[(y * w + x) * 4 + 3];
            if (alpha > 10) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        // Si no hay píxeles (firma vacía), devolver el canvas completo
        if (maxX < minX || maxY < minY) return srcCanvas.toDataURL("image/png");

        // Añadir padding para que no quede pegada al borde
        const pad = 8;
        minX = Math.max(0, minX - pad);
        minY = Math.max(0, minY - pad);
        maxX = Math.min(w, maxX + pad);
        maxY = Math.min(h, maxY + pad);

        const cropW = maxX - minX;
        const cropH = maxY - minY;

        // Dibujar la zona recortada en un canvas temporal
        const tmp = document.createElement("canvas");
        tmp.width  = cropW;
        tmp.height = cropH;
        const tmpCtx = tmp.getContext("2d");
        tmpCtx.drawImage(srcCanvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
        return tmp.toDataURL("image/png");
      })();
      const pngBytes = await (await fetch(dataURL)).arrayBuffer();
      const png = await pdfDoc.embedPng(pngBytes);

      // 🔧 Lógica diferente según tipo de documento
      if (documentType === "parte_trabajo") {
        // === PARTE DE TRABAJO: Escribir en posiciones específicas ===
        const pw = page.getWidth(), ph = page.getHeight();
        const _sx = sx(pw), _sy = sy(ph);

        // Detectar posición real del recuadro "Firmado" en el PDF
        let firmaBox = null;
        try {
          const detected = await detectLabelPositions(bytes);
          if (detected?.boxes?.firmado) {
            firmaBox = detected.boxes.firmado;
            console.log("✅ Recuadro Firmado detectado:", firmaBox);
          } else {
            console.warn("⚠️ No se detectó recuadro Firmado, usando coordenadas fijas");
          }
        } catch(e) {
          console.warn("⚠️ Error detectando posiciones:", e.message);
        }

        // Cajetín firma — usar posición detectada o fallback a coordenadas fijas
        {
          const rect = firmaBox
            ? { x: firmaBox.x, y: firmaBox.y, w: firmaBox.w, h: firmaBox.h }
            : { x: PLACEMENT.firmaRect.x * _sx, y: PLACEMENT.firmaRect.y * _sy, w: PLACEMENT.firmaRect.w * _sx, h: PLACEMENT.firmaRect.h * _sy };

          if (DEBUG) {
            page.drawRectangle({ x: rect.x, y: rect.y, width: rect.w, height: rect.h, borderColor: rgb(1,0,0), borderWidth: 1 });
          }

          const PAD = 2;
          const sigWmax = rect.w - PAD * 2;
          const sigHmax = rect.h - PAD * 2;
          const ratio = png.height / png.width;
          let drawW = sigWmax, drawH = drawW * ratio;
          if (drawH > sigHmax) { drawH = sigHmax; drawW = drawH / ratio; }
          const sigX = rect.x + PAD + (sigWmax - drawW) / 2;
          const sigY = rect.y + PAD + (sigHmax - drawH) / 2;
          page.drawImage(png, { x: sigX, y: sigY, width: drawW, height: drawH });
        }

        // Valores alineados tras etiquetas
        page.drawText(String(km ?? ""), {
          x: valueXAfterLabel(helvetica, "km") * _sx,
          y: LABELS.km.y * _sy,
          size: VALUE_SIZE, font: helvetica,
        });
        page.drawText(String(hDespl ?? ""), {
          x: valueXAfterLabel(helvetica, "horasDespl") * _sx,
          y: LABELS.horasDespl.y * _sy,
          size: VALUE_SIZE, font: helvetica,
        });
        page.drawText(String(hObra ?? ""), {
          x: valueXAfterLabel(helvetica, "horasObra") * _sx,
          y: LABELS.horasObra.y * _sy,
          size: VALUE_SIZE, font: helvetica,
        });

        // Bloques largos
        drawMultiline(page, helvetica, operarios,
          PLACEMENT.operarios.x * _sx,
          PLACEMENT.operarios.y * _sy,
          PLACEMENT.operarios.w * _sx,
          PLACEMENT.operarios.size,
          PLACEMENT.operarios.leading * _sy
        );
        if (operaciones?.trim()) {
          drawMultiline(page, helvetica, operaciones,
            PLACEMENT.operaciones.x * _sx,
            PLACEMENT.operaciones.y * _sy,
            PLACEMENT.operaciones.w * _sx,
            PLACEMENT.operaciones.size,
            PLACEMENT.operaciones.leading * _sy
          );
        }

        // Datos técnico
        page.drawText(name, { x: PLACEMENT.nombre.x * _sx, y: PLACEMENT.nombre.y * _sy, size: PLACEMENT.nombre.size, font: helvetica });
        page.drawText(dni,   { x: PLACEMENT.dni.x    * _sx, y: PLACEMENT.dni.y    * _sy, size: PLACEMENT.dni.size,    font: helvetica });

        // Solo "Fecha:" en el recuadro de firma (nombre y DNI ya están en Datos técnico)
        {
          const s = PLACEMENT.signedInfo.size;
          const xc = (PLACEMENT.signedInfo.xCenter * _sx) || pw / 2;
          const y0 = PLACEMENT.signedInfo.y * _sy;
          const l3 = `Fecha: ${formatDate(new Date())}`;
          const w3 = helvetica.widthOfTextAtSize(l3, s);
          page.drawText(l3, { x: xc - w3 / 2, y: y0, size: s, font: helvetica });
        }
      } else {
        // === ALBARÁN: usar detección automática igual que partes ===
        let albaranBox = null;
        try {
          const detected = await detectLabelPositions(bytes);
          if (detected?.boxes?.firmado) {
            albaranBox = detected.boxes.firmado;
            console.log("✅ Recuadro Firmado (albarán) detectado:", albaranBox);
          } else {
            console.warn("⚠️ No detectado, usando coordenadas fijas");
          }
        } catch(e) {
          console.warn("⚠️ Error detección:", e.message);
        }

        const { x: fx, y: fy, rectW: frW, rectH: frH } = getSignatureRectForPage(page);
        const x    = albaranBox ? albaranBox.x : fx;
        const y    = albaranBox ? albaranBox.y : fy;
        const rectW = albaranBox ? albaranBox.w : frW;
        const rectH = albaranBox ? albaranBox.h : frH;

        if (DEBUG) {
          page.drawRectangle({ x, y, width: rectW, height: rectH, borderColor: rgb(1,0,0), borderWidth: 0.8 });
        }

        const PAD = PDF_TEMPLATE.LAYOUT.PADDING;
        const FONT_SIZE = PDF_TEMPLATE.LAYOUT.FONT_SIZE;
        const LEADING = PDF_TEMPLATE.LAYOUT.LINE_HEIGHT;
        const GAP = PDF_TEMPLATE.LAYOUT.GAP;
        const textH = PAD + LEADING * 3 + PAD;
        const sigAreaH = Math.max(12, rectH - textH - GAP);
        const sigAreaW = rectW - PAD * 2;

        // Firma centrada — llena el área disponible al máximo
        const ratio = png.height / png.width;
        let drawW = sigAreaW, drawH = drawW * ratio;
        if (drawH > sigAreaH) { drawH = sigAreaH; drawW = drawH / ratio; }
        const sigX = x + PAD + (sigAreaW - drawW) / 2;
        const sigY = y + rectH - drawH - PAD - (sigAreaH - drawH) / 2;
        page.drawImage(png, { x: sigX, y: sigY, width: drawW, height: drawH });

        // Texto a la derecha
        const TEXT_RIGHT_PAD = PDF_TEMPLATE.LAYOUT.TEXT_RIGHT_PAD;
        const rightEdge = x + rectW - TEXT_RIGHT_PAD;
        const line1 = `Firmado por: ${name}`;
        const line2 = `DNI: ${dni}`;
        const line3 = `Fecha: ${formatDate(new Date())}`;
        const w1 = helvetica.widthOfTextAtSize(line1, FONT_SIZE);
        const w2 = helvetica.widthOfTextAtSize(line2, FONT_SIZE);
        const w3 = helvetica.widthOfTextAtSize(line3, FONT_SIZE);
        const textBaseY = y + PAD;
        page.drawText(line1, { x: rightEdge - w1, y: textBaseY + LEADING * 2, size: FONT_SIZE, font: helvetica, color: rgb(0,0,0) });
        page.drawText(line2, { x: rightEdge - w2, y: textBaseY + LEADING * 1, size: FONT_SIZE, font: helvetica, color: rgb(0,0,0) });
        page.drawText(line3, { x: rightEdge - w3, y: textBaseY,              size: FONT_SIZE, font: helvetica, color: rgb(0,0,0) });
      }

      // Guardar/subir
      const signedBytes = await pdfDoc.save();
      const signedPath = buildSignedPath(id);
      await uploadBytes(ref(storage, signedPath), new Blob([signedBytes], { type: "application/pdf" }));
      const signedURL = await getDownloadURL(ref(storage, signedPath));
      setDoneURL(signedURL);

      // Actualizar documento en Firestore
      const updateData = {
        status: REQUEST_STATUS.SIGNED,
        signerName: name,
        signerDni: dni,
        signedAt: serverTimestamp(),
        signedPath,
        // 🏗️ NO guardamos obra aquí, ya está guardada desde Upload
      };

      // 🔧 Añadir campos de parte de trabajo si aplica
      if (documentType === "parte_trabajo") {
        updateData.kmTotales = String(km ?? "");
        updateData.horasDespl = String(hDespl ?? "");
        updateData.horasObra = String(hObra ?? "");
        updateData.operarios = String(operarios ?? "");
        updateData.operaciones = String(operaciones ?? "");
      }

      await updateDoc(doc(db, COLLECTIONS.REQUESTS, id), updateData);

      // 🔗 WEBHOOK: Notificar al Kanban CEJM si el albarán viene de allí
      if (documentType === "albaran" && request?.originalRequestId) {
        try {
          console.log("🔗 Verificando si hay que notificar al Kanban...");
          
          // Buscar la solicitud original de albarán
          const solicitudRef = doc(db, "albaran_requests", request.originalRequestId);
          const solicitudSnap = await getDoc(solicitudRef);
          
          if (solicitudSnap.exists()) {
            const solicitudData = solicitudSnap.data();
            
            // Solo enviar webhook si la solicitud viene desde Kanban
            if (solicitudData.origen === 'kanban-cejm' && solicitudData.kanban_card_id) {
              console.log("🔗 Solicitud desde Kanban detectada, enviando webhook...");
              
              // Preparar datos del webhook
              const webhookData = {
                kanban_card_id: solicitudData.kanban_card_id,
                solicitud_id: request.originalRequestId,
                num_pedido: solicitudData.numeroPedido || request.numeroPedido || 'N/A',
                num_serie: solicitudData.numeroSerie || request.numeroSerie || 'N/A',
                firmado_por: name,
                fecha_firma: new Date().toISOString(),
                url_documento: signedURL
              };
              
              // Enviar webhook al Kanban
              const WEBHOOK_URL = import.meta.env.VITE_KANBAN_WEBHOOK_URL || 'https://webhook-cejm.ngrok.app/api/kanban/albaran-firmado';
              const WEBHOOK_SECRET = import.meta.env.VITE_KANBAN_WEBHOOK_SECRET || 'default-secret';
              
              try {
                const webhookResponse = await fetch(WEBHOOK_URL, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Webhook-Secret': WEBHOOK_SECRET
                  },
                  body: JSON.stringify(webhookData)
                });
                
                if (webhookResponse.ok) {
                  console.log("✅ Webhook enviado exitosamente al Kanban");
                  
                  // Registrar el envío exitoso en la solicitud
                  await updateDoc(solicitudRef, {
                    webhookSent: true,
                    webhookSentAt: serverTimestamp(),
                    webhookResponse: 'success'
                  });
                } else {
                  const errorText = await webhookResponse.text().catch(() => 'No response');
                  console.error("❌ Error en webhook al Kanban:", webhookResponse.status, errorText);
                  
                  // Registrar el error para reintento posterior
                  await updateDoc(solicitudRef, {
                    webhookSent: false,
                    webhookError: `HTTP ${webhookResponse.status}: ${errorText}`,
                    webhookFailedAt: serverTimestamp(),
                    webhookRetryNeeded: true
                  });
                  
                  showToast({ 
                    type: "warning", 
                    message: "Documento firmado, pero no se pudo notificar al Kanban" 
                  });
                }
              } catch (webhookError) {
                console.error("❌ Error enviando webhook al Kanban:", webhookError);
                
                // Registrar el error para reintento posterior
                await updateDoc(solicitudRef, {
                  webhookSent: false,
                  webhookError: webhookError.message || String(webhookError),
                  webhookFailedAt: serverTimestamp(),
                  webhookRetryNeeded: true
                });
                
                showToast({ 
                  type: "warning", 
                  message: "Documento firmado, pero hubo un error notificando al Kanban" 
                });
              }
            } else {
              console.log("ℹ️ Solicitud no viene desde Kanban, no se envía webhook");
            }
            
            // Actualizar estado de la solicitud a completada (para todos los albaranes)
            await updateDoc(solicitudRef, {
              status: "completada",
              completedAt: serverTimestamp(),
              signedAlbaranId: id
            });
            
            console.log("✅ Estado de solicitud de albarán actualizado a 'completada'");
          } else {
            console.warn("⚠️ La solicitud original de albarán no existe:", request.originalRequestId);
          }
        } catch (error) {
          console.error("❌ Error procesando webhook/solicitud de albarán:", error);
        }
      }

      // 🔄 Actualizar estado de la solicitud original si existe referencia (PARTES DE TRABAJO)
      console.log("🔍 DEBUG - Verificando originalRequestId:", request?.originalRequestId);
      console.log("🔍 DEBUG - Request completo:", request);
      
      if (request?.originalRequestId) {
        try {
          console.log("🔄 Actualizando solicitud original de parte de trabajo:", request.originalRequestId);
          const solicitudRef = doc(db, "work_requests", request.originalRequestId);
          
          // Verificar que la solicitud existe antes de actualizarla
          const solicitudSnap = await getDoc(solicitudRef);
          if (solicitudSnap.exists()) {
            await updateDoc(solicitudRef, {
              status: "completada",
              completedAt: serverTimestamp(),
              signedParteTrabajoId: id, // ID del parte de trabajo firmado
            });
            
            console.log("✅ Estado de solicitud actualizado a 'completada'");
          } else {
            console.warn("⚠️ La solicitud original no existe:", request.originalRequestId);
          }
        } catch (error) {
          console.error("❌ Error actualizando estado de solicitud a completada:", error);
          console.error("❌ Stack trace:", error.stack);
          
          // Intentar actualización alternativa usando setDoc con merge
          try {
            console.log("🔄 Intentando actualización alternativa...");
            const solicitudRef = doc(db, "work_requests", request.originalRequestId);
            await setDoc(solicitudRef, {
              status: "completada",
              completedAt: serverTimestamp(),
              signedParteTrabajoId: id,
            }, { merge: true });
            
            console.log("✅ Estado actualizado con setDoc merge");
          } catch (altError) {
            console.error("❌ Error en actualización alternativa:", altError);
          }
        }
      } else {
        console.log("⚠️ No se encontró originalRequestId en el request");
      }

      // Enviar emails con el documento firmado
      const recipientEmails = request?.recipients?.filter(r => r.type === "email").map(r => r.value) || [];
      const clientEmails = request?.finalEmails || [];

      // Obtener el email del uploader desde el request (no necesitamos consultar users)
      const uploaderEmail = request?.uploaderEmail || null;

      // 📧 Lógica de emails según tipo de documento
      let allEmailsArray = [];
      if (documentType === "parte_trabajo") {
        // Para partes de trabajo: SOLO cliente (finalEmails) y ptrabajo@cejm.eu
        allEmailsArray.push("ptrabajo@cejm.eu");
        allEmailsArray.push(...clientEmails);
        if (request?.emailCliente) allEmailsArray.push(request.emailCliente);
      } else {
        // Para albaranes: destinatarios + cliente + uploader
        allEmailsArray = [...recipientEmails, ...clientEmails];
        if (uploaderEmail) allEmailsArray.push(uploaderEmail);
        if (request?.emailCliente) allEmailsArray.push(request.emailCliente);
      }
      const allEmails = [...new Set(allEmailsArray)]; // Eliminar duplicados

      if (allEmails.length > 0 && (SEND_SIGNED_URL || SEND_URL)) {
        console.log("📧 DEBUG - Enviando emails...");
        
        // Para firmantes públicos usar requestId como auth alternativa
        // Solo usar token si hay usuario autenticado no anónimo
        let idToken = null;
        try {
          const user = auth.currentUser;
          if (user && !user.isAnonymous) {
            idToken = await user.getIdToken(true);
          }
          // Si no hay usuario o es anónimo, la Cloud Function usará requestId
        } catch (authError) {
          console.warn("⚠️ No se pudo obtener token:", authError.message);
        }

        {
          const emailHtml = buildSignedDocumentEmail({
            documentName: request.originalName || "documento",
            signerName: name,
            signerDni: dni,
            signedDate: formatDate(new Date()),
            downloadUrl: signedURL,
          });

          // Usar la nueva función que adjunta el PDF si está disponible
          const sendUrl = SEND_SIGNED_URL || SEND_URL;
          const useSendSigned = !!SEND_SIGNED_URL;

          console.log("📧 DEBUG - URL de envío:", sendUrl);
          console.log("📧 DEBUG - Usar sendSigned:", useSendSigned);

          try {
            const authHeaders = idToken 
              ? { "Authorization": `Bearer ${idToken}` }
              : {};
            const res = await fetch(sendUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...authHeaders },
              body: JSON.stringify({
                to: allEmails,
                subject: request.originalName 
                  ? `${request.originalName}`
                  : `${documentType === "parte_trabajo" ? "Parte de Trabajo" : "Albarán"} Firmado`,
                html: emailHtml,
                requestId: id, // Auth alternativa para firmantes sin sesión
                ...(useSendSigned && { signedPath, attachPdf: true, originalName: request?.originalName || null }),
              }),
            });

            const data = await res.json().catch(() => ({}));
            console.log("📧 DEBUG - Respuesta del servidor:", data);
            
            if (!res.ok || !data.ok) {
              console.error("Error enviando correos:", data);
              showToast({ 
                type: "warning", 
                message: "El documento se firmó, pero no se pudieron enviar los correos" 
              });
            } else {
              const attached = data.attached ? " con adjunto" : "";
              showToast({ 
                type: "success", 
                message: `¡Documento firmado y enviado${attached} a ${allEmails.length} destinatario(s)!` 
              });
            }
          } catch (fetchError) {
            console.error("📧 Error en fetch de email:", fetchError);
            showToast({ 
              type: "warning", 
              message: "Documento firmado, pero hubo un error enviando los correos" 
            });
          }
        }
      } else {
        console.log("📧 DEBUG - No hay emails para enviar o falta configuración");
        console.log("📧 DEBUG - SEND_SIGNED_URL:", SEND_SIGNED_URL);
        console.log("📧 DEBUG - SEND_URL:", SEND_URL);
        showToast({ type: "success", message: "¡Documento firmado correctamente!" });
      }
    } catch (e) {
      console.error("Error al firmar:", e);
      showToast({ type: "error", message: `Error al firmar: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ padding: 24, background: '#f1f5f9', minHeight: '100vh' }}>Cargando…</p>;
  if (!pdfURL && !doneURL) return <p style={{ padding: 24, background: '#f1f5f9', minHeight: '100vh' }}>No se pudo cargar el PDF.</p>;

  return (
    <div className="container light-content" ref={containerRef} data-mobile={isMobile ? "1" : "0"}
      style={{ background: '#f1f5f9', minHeight: '100vh', color: '#0f172a' }}>
      <h1 className="title" style={{ color: '#0f172a' }}>Revisar y firmar</h1>

      <div className="card grid" style={{ background: '#ffffff', border: '1px solid #e2e8f0', color: '#0f172a' }}>
        {/* 📋 Recuadro informativo del documento - para que el operario sepa qué firma */}
        {request && !doneURL && (
          <div style={{
            background: '#eff6ff',
            border: '2px solid #3b82f6',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 4,
          }}>
            <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#1e40af', fontSize: 15 }}>
              📄 Documento a firmar
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
              {request.originalName && (
                <div>
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>Nombre</span>
                  <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{request.originalName}</span>
                </div>
              )}
              {(obra || codigoObra) && (
                <div>
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>Obra</span>
                  <span style={{ fontWeight: 600, color: '#1d4ed8', fontSize: 14 }}>{obra || codigoObra}</span>
                </div>
              )}
              {fechasActuacion && (
                <div>
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>Fechas de actuación</span>
                  <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{fechasActuacion}</span>
                </div>
              )}
              {partidaTrabajo && (
                <div>
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>Partida</span>
                  <span style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>{partidaTrabajo}</span>
                </div>
              )}
              {direccionObra && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>Dirección</span>
                  <span style={{ fontWeight: 500, color: '#374151', fontSize: 13 }}>{direccionObra}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PDF: en móvil SOLO botón; en escritorio SOLO iframe. Forzamos por inline style. */}
        {(pdfURL || doneURL) && (
          isMobile ? (
            <a
              className="pdf-opener copy"
              style={{ display: "inline-flex" }}
              href={(doneURL || pdfURL) ?? "#"}
              target="_blank"
              rel="noreferrer"
            >
              Abrir PDF
            </a>
          ) : (
            <iframe
              className="pdf-frame"
              title={request?.originalName || "documento"}
              src={(doneURL || pdfURL) + "#toolbar=1"}
              style={{ display: "block", width: "100%", height: "72vh", border: "1px solid var(--primary)", borderRadius: 12 }}
            />
          )
        )}

        {request?.status !== "signed" && (
          <div className="grid">
            {/* 🔧 Campos adicionales para Partes de Trabajo */}
            {documentType === "parte_trabajo" && (
              <>
                <div className="form-grid-3" style={{ marginTop: 12 }}>
                  <input placeholder="Kilómetros totales *" value={km} onChange={(e) => setKm(e.target.value)} style={{ borderColor: !km.trim() ? '#ef4444' : undefined }} />
                  <input placeholder="Horas desplazamiento (por operario) *" value={hDespl} onChange={(e) => setHDespl(e.target.value)} style={{ borderColor: !hDespl.trim() ? '#ef4444' : undefined }} />
                  <input placeholder="Horas obra (por operario) *" value={hObra} onChange={(e) => setHObra(e.target.value)} style={{ borderColor: !hObra.trim() ? '#ef4444' : undefined }} />
                </div>

                <textarea
                  placeholder="Operarios * (p. ej. Juan 3h; Ana 2h)"
                  value={operarios}
                  onChange={(e) => setOperarios(e.target.value)}
                  rows={2}
                  style={{ marginTop: 8, borderColor: !operarios.trim() ? '#ef4444' : undefined }}
                />
                <textarea
                  placeholder="Operaciones / observaciones (opcional)"
                  value={operaciones}
                  onChange={(e) => setOperaciones(e.target.value)}
                  rows={2}
                  style={{ marginTop: 8 }}
                />
                <p className="meta" style={{ marginTop: 4, fontSize: 12, color: '#ef4444' }}>* Campos obligatorios</p>
              </>
            )}

            {/* 🏗️ Mostrar Obra - Solo lectura */}
            {obra && (
              <div style={{ marginTop: 12, padding: '12px', backgroundColor: '#0f1536', border: '1px solid #4f6cf7', borderRadius: '10px' }}>
                <p className="kicker" style={{ margin: '0 0 4px 0' }}>Obra</p>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#e9eefc' }}>{obra}</p>
              </div>
            )}

            <div className="form-grid-2">
              <input placeholder="Nombre completo" value={name} onChange={(e) => setName(e.target.value)} />
              <input placeholder="DNI" value={dni} onChange={(e) => setDni(e.target.value)} />
            </div>

            {/* 📋 Información pre-rellenada desde solicitud */}
            {(codigoObra || partidaTrabajo || fechasActuacion || personaContacto || trabajosRealizar) && (
              <div style={{ 
                background: '#0f1536', 
                padding: '16px', 
                borderRadius: '10px', 
                border: '1px solid #10b981',
                marginTop: '16px'
              }}>
                <p style={{ margin: '0 0 12px 0', color: '#10b981', fontWeight: 'bold' }}>
                  📋 Información del Trabajo (pre-rellenada)
                </p>
                
                <div className="form-grid-2" style={{ gap: '12px' }}>
                  {codigoObra && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Código de Obra</p>
                      <p style={{ margin: 0, fontWeight: 'bold', color: '#fbbf24' }}>{codigoObra}</p>
                    </div>
                  )}
                  
                  {partidaTrabajo && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Partida de Trabajo</p>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{partidaTrabajo}</p>
                    </div>
                  )}
                  
                  {fechasActuacion && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Fecha de Actuación</p>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{fechasActuacion}</p>
                    </div>
                  )}
                  
                  {numeroOferta && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Nº Oferta/Pedido</p>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{numeroOferta}</p>
                    </div>
                  )}
                  
                  {personaContacto && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Persona de Contacto</p>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{personaContacto}</p>
                    </div>
                  )}
                  
                  {direccionObra && (
                    <div>
                      <p className="kicker" style={{ margin: '0 0 4px 0' }}>Dirección de la Obra</p>
                      <p style={{ margin: 0, fontWeight: 'bold' }}>{direccionObra}</p>
                    </div>
                  )}
                </div>
                
                {trabajosRealizar && (
                  <div style={{ marginTop: '12px' }}>
                    <p className="kicker" style={{ margin: '0 0 4px 0' }}>Trabajos a Realizar</p>
                    <div style={{ 
                      background: '#0d1330', 
                      padding: '8px 12px', 
                      borderRadius: '6px',
                      border: '1px solid #273067'
                    }}>
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                        {trabajosRealizar}
                      </p>
                    </div>
                  </div>
                )}
                
                <p className="meta" style={{ margin: '12px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                  ℹ️ Esta información se cargó automáticamente desde la solicitud enviada
                </p>
              </div>
            )}

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                <p className="kicker" style={{ margin: 0 }}>Tu firma</p>
                <button className="copy" type="button" onClick={() => setFsOpen(true)}>Pantalla completa</button>
              </div>

              <div className="sig-box" ref={sigBoxRef}>
                <canvas
                  ref={nativeCanvasRef}
                  style={{
                    display: "block",
                    touchAction: "none",
                    cursor: "crosshair",
                    borderRadius: "10px",
                    background: "white",
                  }}
                  onMouseDown={onPointerDown}
                  onMouseMove={onPointerMove}
                  onMouseUp={onPointerUp}
                  onMouseLeave={onPointerUp}
                  onTouchStart={onPointerDown}
                  onTouchMove={onPointerMove}
                  onTouchEnd={onPointerUp}
                />
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                <button className="copy" onClick={clearNativeCanvas}>Limpiar</button>
                <button className="btn" disabled={saving} onClick={sign}>{saving ? "Guardando…" : "Firmar y enviar"}</button>
              </div>
            </div>
          </div>
        )}

        {doneURL && (
          <div>
            <p className="kicker">PDF firmado</p>
            <div className="linkbox">
              <input value={doneURL} readOnly onFocus={(e) => e.target.select()} />
              <a className="copy" href={doneURL} target="_blank" rel="noreferrer">Abrir</a>
            </div>
            <p className="meta">También puedes descargarlo desde el enlace.</p>

            <div style={{ marginTop: 10, display: "flex", gap: 12 }}>
              <button className="btn" type="button" onClick={shareWhatsApp}>Enviar por WhatsApp</button>
            </div>

            {/* Campo para enviar por email al cliente */}
            <div style={{ marginTop: 20 }}>
              <p className="kicker">📧 Enviar documento firmado al cliente</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="email"
                  placeholder="correo del cliente"
                  value={emailToSend}
                  onChange={(e) => setEmailToSend(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter' && !sendingEmail) sendByEmail(); }}
                  disabled={sendingEmail}
                  style={{ flex: 1, minWidth: '200px' }}
                />
                <button
                  className="btn"
                  type="button"
                  onClick={sendByEmail}
                  disabled={sendingEmail || !emailToSend.trim()}
                  style={{ opacity: (sendingEmail || !emailToSend.trim()) ? 0.5 : 1 }}
                >
                  {sendingEmail ? "📨 Enviando..." : "📧 Enviar"}
                </button>
              </div>
              <p className="meta" style={{ marginTop: 8 }}>
                Añade el correo del cliente para enviarle el documento firmado
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de firma a pantalla completa */}
      {fsOpen && (
        <div className="fs-backdrop" role="dialog" aria-label="Firma en pantalla completa">
          <div className="fs-header">
            <button className="copy" onClick={() => fsSigRef.current?.clear()}>Limpiar</button>
            <button className="btn" onClick={takeFsSignature}>Usar esta firma</button>
            <button className="copy" onClick={() => setFsOpen(false)}>Cerrar</button>
          </div>
          <div className="fs-body">
            <SignatureCanvas
              ref={fsSigRef}
              penColor="#000"
              backgroundColor="transparent"
              canvasProps={{ 
                width: 800,
                height: 400,
                className: "sig-canvas", 
                style: { 
                  width: "100%", 
                  height: "100%", 
                  touchAction: "none" 
                } 
              }}
            />
          </div>
        </div>
      )}
      <style>{`
        .light-content .card, .light-content .grid { background: #fff !important; color: #0f172a !important; }
        .light-content input, .light-content textarea, .light-content select {
          background: #f9fafb !important; color: #111827 !important; border: 1px solid #d1d5db !important; border-radius: 10px !important;
        }
        .light-content input::placeholder, .light-content textarea::placeholder { color: #9ca3af !important; }
        .light-content .kicker, .light-content .meta { color: #64748b !important; }
        .light-content .title, .light-content h1, .light-content h2 { color: #0f172a !important; }
        .light-content .btn { background: #1e293b !important; color: #fff !important; }
        .light-content .copy { background: #f8fafc !important; border: 1px solid #cbd5e1 !important; color: #374151 !important; }
        .light-content .sig-canvas { background: #fff !important; border-radius: 8px; }
      `}</style>
    </div>
  );
}